import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { SocketService, CaregiverService } from '../../services';
import { Subscription } from 'rxjs/Subscription';
import { distinctUntilChanged } from 'rxjs/operators';
import { NgZone } from '@angular/core';
import { BandModel, showsWearStatus } from '../../models/band-model';
import { hasActiveBattery, isContactStale, isDischargedBattery } from '../../models/band-status';

@Component({
  selector: 'app-check-status-user',
  templateUrl: './check-status-user.component.html',
  styleUrls: ['./check-status-user.component.scss']
})
export class CheckStatusUserComponent implements OnInit, OnDestroy {

  @Input() id;
  @Input() size;
  @Input() initial;
  @Input() user?: any;
  @Input() useLastContact?: boolean = false;
  /**
   * Model wyliczony przez rodzica. Surowe `user.bandType` (type_hardware) samo nie
   * wystarcza: Easy Care ma tam raz 'EASY_CARE', a raz — bo sprzetowo jest Vitalem —
   * 'CARE', i wtedy bylby nieodrozninalny od Care Pro.
   */
  @Input() model: BandModel = 'UNKNOWN';
  /**
   * Czyj to status. Bez `user` komponent pokazuje obecność z socketa — dla opiekuna
   * to status OSOBY ("Aktywny"), nie opaski ("Aktywna"). Domyślnie 'band', żeby
   * dotychczasowe użycia przy podopiecznych nie zmieniły podpisu.
   */
  @Input() subject: 'band' | 'person' = 'band';

  @Output() changeStatus: EventEmitter<Object> = new EventEmitter<Object>();
  private statuses$: Subscription;
  private inactivityTimeout$: Subscription;
  private inactivityTimeoutHours: number = null;
  public status: boolean;

  constructor(
    private socketService: SocketService,
    private caregiverService: CaregiverService,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    this.status = this.initial;

    // Najpierw ostatnio znana wartość (synchronicznie), potem potwierdzenie z API
    this.inactivityTimeoutHours = this.caregiverService.getCachedInactivityTimeoutHours();
    this.inactivityTimeout$ = this.caregiverService.getInactivityTimeoutHours()
      .subscribe(hours => {
        this.inactivityTimeoutHours = hours;
      });

    this.ngZone.runOutsideAngular(() => {
      this.statuses$ = this.socketService.onStatus()
        .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        .subscribe(statuses => {
          const isThere = statuses.includes(this.id);

          if (this.status !== isThere) {
            this.ngZone.run(() => {
              this.status = isThere;
              this.changeStatus.emit(this.status);
            });
          }
        });
    });
  }

  private get referenceTimestamp(): number | string {
    if (!this.user) return 0;

    if (this.useLastContact === true) {
      return this.user.last_contact;
    }

    return this.user.lastMeasurement;
  }

  /**
   * Status opaski zależy od progu nieaktywności - dopóki go nie znamy, nie renderujemy
   * niczego, zamiast pokazywać wartość policzoną z domyślnych 12 h i za chwilę ją zmieniać.
   */
  get statusReady(): boolean {
    return !this.user || this.inactivityTimeoutHours !== null;
  }

  /**
   * Kody sim_error / stc_error / eeprom_cleared idą do wspólnego, neutralnego
   * komunikatu — podopieczny nie ma się czym martwić, a dokładny powód i tak
   * zostaje w band.powered_off_reason dla supportu.
   *
   * Nieznany kod też trafia do TECHNICAL: gdy firmware dorzuci szóstą ramkę
   * wyłączeniową, kafelek pokaże sensowny status zamiast pustego stringa.
   */
  private get offStatusKey(): string {
    switch (this.user.powered_off_reason) {
      case 'user':
        return 'USER.STATUS.OFF-USER';
      case 'low_voltage':
        return 'USER.STATUS.OFF-BATTERY';
      default:
        return 'USER.STATUS.OFF-TECHNICAL';
    }
  }

  get bandStatusKey(): string {
    if (!this.user) {
      return this.status ? 'USER.STATUS.ACTIVE' : 'USER.STATUS.NOACTIVE';
    }

    const dateNow = Date.now();
    const ref = this.referenceTimestamp;

    // Wyłączona wygrywa z "brak kontaktu" bezterminowo: to jest ODPOWIEDŹ na
    // pytanie, czemu cisza, a nie tylko stwierdzenie, że cisza jest.
    // Świadomie bez gate'a na bandType — ramki wyłączeniowe pochodzą z REVJ,
    // a wszystkie gałęzie niżej są zawężone do 'CARE' i by je ukryły.
    if (this.user.powered_off_at) {
      return this.offStatusKey;
    }

    if (isContactStale(ref, this.inactivityTimeoutHours, dateNow)) {
      return 'USER.STATUS.NOACTIVE';
    }
    if (isDischargedBattery(this.user)) {
      return 'USER.STATUS.DISCHARDED';
    }
    // Zaden socket event nie gasi is_charge po dojsciu do 100% (patrz startCharge
    // w socket.service.ts) — zostaje true az do kolejnego startUsing/stopUsing.
    // Bez tej galezi opaska w pelni naladowana, ale wciaz lezaca na ladowarce,
    // pokazywalaby w nieskonczonosc "Na ladowarce" zamiast "Naladowana".
    if (this.user.is_charge && this.user.batteryLevel >= 100) {
      return 'USER.STATUS.ACTIVE';
    }
    // status=true -> opaska nadal na rece mimo ladowania (rzadki przypadek: kabel
    // podpiety na noszonej opasce) -> ogolne "Ladowanie". status=false -> opaska
    // zdjeta i lezy na ladowarce -> "Na ladowarce" (ONCHARGER), typowy przypadek.
    // Przed sprawdzeniem baterii: opaska na ladowarce z 0% laduje sie, nie jest nieaktywna.
    if (this.user.is_charge) {
      return this.user.status ? 'USER.STATUS.CHARGING' : 'USER.STATUS.ONCHARGER';
    }
    if (!hasActiveBattery(this.user.batteryLevel)) {
      return 'USER.STATUS.NOACTIVE';
    }
    if (this.user.bandType === 'CARE' && showsWearStatus(this.model)) {
      return this.user.status ? 'USER.STATUS.WORN' : 'USER.STATUS.REMOVED';
    }
    return 'USER.STATUS.ACTIVE';
  }

  get bandStatusClass(): string {
    if (!this.user) {
      return this.status ? 'active' : 'no-active';
    }

    switch (this.bandStatusKey) {
      case 'USER.STATUS.NOACTIVE':
      case 'USER.STATUS.DISCHARDED':
        return 'noactive';
      case 'USER.STATUS.OFF-USER':
      case 'USER.STATUS.OFF-BATTERY':
      case 'USER.STATUS.OFF-TECHNICAL':
        return 'off';
      case 'USER.STATUS.CHARGING':
      case 'USER.STATUS.ONCHARGER':
        return 'charging';
      case 'USER.STATUS.WORN':
      case 'USER.STATUS.ACTIVE':
        return 'worn';
      case 'USER.STATUS.REMOVED':
        return 'removed';
      default:
        return 'worn';
    }
  }

  /**
   * Ikona zamiast samej kropki — tylko dla statusu opaski (`user` podany).
   * Status online/offline opiekuna (bez `user`, klasy 'active'/'no-active') zostaje
   * kropka, bo to nie jest kwestia baterii i ikonka by wprowadzala w blad.
   */
  get statusIcon(): string | null {
    if (!this.user) {
      return null;
    }

    switch (this.bandStatusClass) {
      case 'charging':
        return 'charging';
      case 'worn':
      case 'active':
        return 'full-battery';
      case 'noactive':
        // 'noactive' pokrywa TRZY różne przyczyny (patrz bandStatusOf/bandStatusKey):
        // przekroczony próg nieaktywności, brak kontaktu od nowa dodanej opaski (batteryLevel
        // nigdy nie przyszedł — undefined) i realnie zgłoszone 0–15%. Ikonę/tekst "rozładowana"
        // pokazujemy tylko w tym ostatnim przypadku — dla pozostałych dwóch bateria może
        // być w porządku, a "Rozładowana" byłoby zwykłym zgadywaniem.
        return this.isDischarged ? 'empty-battery' : null;
      default:
        return null; // 'removed', 'off'
    }
  }

  /**
   * Dla szablonu: 'noactive' pokrywa trzy przyczyny, ale tylko realnie zgloszone
   * 0–15% zasluguje na czerwona pigulke. Pozostale dwa to "brak kontaktu" i ida
   * na szaro — inaczej pigulka krzyczalaby o pustej baterii, ktorej nie znamy.
   */
  get isDischarged(): boolean {
    return this.bandStatusKey === 'USER.STATUS.DISCHARDED';
  }

  /**
   * Tekst pigulki. Zrodlem jest bandStatusKey — to ON zna priorytety (wylaczona >
   * brak kontaktu > rozladowana > ladowanie > noszenie). Podmieniamy tylko DWA
   * stany czysto bateryjne na plaskie klucze 'BACKEND.Band ...' (przetlumaczone
   * juz we wszystkich jezykach).
   *
   * Do 2026-09 tekst wybieral tu switch po statusIcon, a ikona idzie z KLASY, nie
   * ze statusu: klasa 'worn' ma ikone 'full-battery', wiec KAZDA zalozona opaska
   * (MEAS 'Band worn hard', zwykla ramka pomiarowa z tetnem) byla podpisana
   * "Naladowana" zamiast "Zalozona" — nawet przy BATT:77% i bez ladowarki.
   * Bledne bylo TYLKO podpisanie: API ustawia flagi dobrze (InfoHandler::bandWorn
   * i MeasureHandler daja is_wear=1, is_charge=0). Przy okazji 'Ladowanie'
   * i 'Na ladowarce' nie splaszczaja sie juz do jednego tekstu.
   */
  get displayStatusKey(): string {
    const key = this.bandStatusKey;

    // Status osoby (opiekun): ta sama kropka i pigułka, inny rodzaj gramatyczny podpisu.
    if (!this.user && this.subject === 'person') {
      return key === 'USER.STATUS.ACTIVE' ? 'COMMON.ACTIVE' : 'COMMON.INACTIVE';
    }

    if (key === 'USER.STATUS.DISCHARDED') {
      return 'BACKEND.Band discharged';
    }

    // Jedyne ACTIVE z is_charge to galaz "na ladowarce i 100%" z bandStatusKey;
    // "Naladowana" mowi tam wiecej niz ogolne "Aktywna". Zalozona opaska ma
    // is_charge=false i zostaje przy swoim WORN.
    if (key === 'USER.STATUS.ACTIVE' && this.user && this.user.is_charge) {
      return 'BACKEND.Band fullcharged';
    }

    return key;
  }

  ngOnDestroy() {
    if (this.statuses$) {
      this.statuses$.unsubscribe();
    }
    if (this.inactivityTimeout$) {
      this.inactivityTimeout$.unsubscribe();
    }
  }
}
